import { useEffect, useState } from 'react';

import { Col, Form } from 'antd';
import { Formik, FormikProps } from 'formik';
import { isUndefined } from 'lodash';
import { useDispatch, useSelector } from 'react-redux';
import {
  doClearCreateAccountHolderDisclosures,
  doClearPatchAccountHolderDisclosures,
  doCreateAccountHolderDisclosures,
  doPatchAccountHolderDisclosures,
} from 'src/actions';
import { AccountHolderStatementDeliveryDto, PatchAccountHolderDisclosureBodyDto } from 'src/dtos';
import { MFormCheckbox, MFormInput, MFormRadioGroup, MFormSaveButton, MRadioGroupOption } from 'src/lib';
import { AccountHolder, AccountHolderStatementDeliveryLabel } from 'src/models';
import { Spacing } from 'src/styles';
import { assertNonNullable } from 'src/utils';
import * as Yup from 'yup';

import * as Styles from './Disclosures.styles';

export const upsertAccountHolderDisclosuresValidation = Yup.object().shape({
  statementDelivery: Yup.string().required('Statement Delivery is required'),
  stakeholder: Yup.boolean().default(false),
  industryEmployed: Yup.boolean().default(false),
  tickerSymbol: Yup.string()
    .when('stakeholder', {
      is: true,
      then: Yup.string().trim().required('Company Ticker Symbol is required'),
    })
    .max(50, 'Company Ticker Symbol can have a maximum length of 50 characters'),
  firmName: Yup.string().when('industryEmployed', {
    is: true,
    then: Yup.string().trim().required('Firm Name is required'),
  }),
});

export const STATEMENT_DELIVERY_OPTION_LIST: MRadioGroupOption[] = [
  {
    label: AccountHolderStatementDeliveryLabel.Electronic,
    value: AccountHolderStatementDeliveryDto.Electronic,
  },
  {
    label: AccountHolderStatementDeliveryLabel.Paper,
    value: AccountHolderStatementDeliveryDto.Paper,
    tooltip: {
      type: 'popover',
      content:
        'Trade Confirmations: $2.00 per confirm Monthly Statement: $5.00 per statement Tax Document: $5.00 per document',
      icon: 'info',
    },
  },
];

export interface DisclosuresFormValues {
  statementDelivery: AccountHolderStatementDeliveryDto;
  stakeholder?: boolean;
  tickerSymbol?: string;
  industryEmployed?: boolean;
  firmName?: string;
}

export interface DisclosuresFormProps {
  accountUuid?: string;
  accountHolder?: AccountHolder;
  isPrimary: boolean;
  onCancel?: () => void;
  onSave?: () => void;
  testId?: 'primary' | 'joint';
}

export const DisclosuresForm = ({
  accountUuid,
  accountHolder,
  isPrimary,
  onCancel,
  onSave,
  testId,
}: DisclosuresFormProps) => {
  const dispatch = useDispatch();

  const isCreateDisclosuresLoading = useSelector((state: any) =>
    Boolean(state.accountHolders.createDisclosures.__requested),
  );
  const succeededCreateDisclosures = useSelector((state: any) =>
    Boolean(state.accountHolders.createDisclosures.__succeeded),
  );

  const isPatchDisclosuresLoading = useSelector((state: any) =>
    Boolean(state.accountHolders.patchDisclosures.__requested),
  );
  const succeededPatchDisclosures = useSelector((state: any) =>
    Boolean(state.accountHolders.patchDisclosures.__succeeded),
  );

  const createRequestData = useSelector((state: any) => state.accountHolders.createDisclosures?.data);
  const patchRequestData = useSelector((state: any) => state.accountHolders.patchDisclosures?.data);

  const [shouldPatch, setShouldPatch] = useState<boolean>(false);

  const isDisclosuresAlreadySaved = () => !isUndefined(accountHolder?.disclosure?.statementDelivery);

  const isRequestMatched = () =>
    accountHolder?.id === createRequestData?.params?.accountHolderId ||
    accountHolder?.id === patchRequestData?.params?.accountHolderId;

  const isUpsertLoading = (isCreateDisclosuresLoading || isPatchDisclosuresLoading) && isRequestMatched();

  const mapViewToDto = (values: DisclosuresFormValues): PatchAccountHolderDisclosureBodyDto => ({
    statementDelivery: values.statementDelivery,
    stakeholder: values.stakeholder === true && values.tickerSymbol ? { tickerSymbol: values.tickerSymbol } : false,
    industryEmployed: values.industryEmployed === true && values.firmName ? { firmName: values.firmName } : false,
  });

  const _onCancel = (form: FormikProps<DisclosuresFormValues>) => {
    form.resetForm();

    if (onCancel) {
      onCancel();
    }
  };

  const _onSave = (form: FormikProps<DisclosuresFormValues>) => {
    form.submitForm();

    if (onSave) {
      onSave();
    }
  };

  useEffect(() => {
    if (isDisclosuresAlreadySaved()) {
      setShouldPatch(true);
    }
  }, [accountHolder]);

  useEffect(() => {
    if ((succeededCreateDisclosures || succeededPatchDisclosures) && isRequestMatched()) {
      if (onCancel) {
        onCancel();
      }
    }
  }, [succeededCreateDisclosures, succeededPatchDisclosures]);

  useEffect(() => {
    return () => {
      dispatch(doClearCreateAccountHolderDisclosures());
      dispatch(doClearPatchAccountHolderDisclosures());
    };
  }, []);

  return (
    <Formik<DisclosuresFormValues>
      enableReinitialize
      validateOnChange
      validateOnBlur
      onSubmit={values => {
        assertNonNullable(accountHolder, 'AccountHolder');
        assertNonNullable(accountUuid, 'accountUuid');

        const dto = mapViewToDto(values);

        if (shouldPatch) {
          dispatch(
            doPatchAccountHolderDisclosures({
              params: {
                id: accountUuid,
                accountHolderId: accountHolder.id,
              },
              body: dto,
            }),
          );

          return;
        }

        dispatch(
          doCreateAccountHolderDisclosures({
            params: {
              id: accountUuid,
              accountHolderId: accountHolder.id,
            },
            body: dto,
          }),
        );
      }}
      initialValues={{
        statementDelivery: isPrimary
          ? accountHolder?.disclosure?.statementDelivery?.value ?? AccountHolderStatementDeliveryDto.Electronic
          : AccountHolderStatementDeliveryDto.Electronic,
        stakeholder: Boolean(accountHolder?.disclosure?.stakeholder),
        tickerSymbol:
          accountHolder?.disclosure?.stakeholder === false ? '' : accountHolder?.disclosure?.stakeholder?.tickerSymbol,
        industryEmployed: Boolean(accountHolder?.disclosure?.industryEmployed),
        firmName:
          accountHolder?.disclosure?.industryEmployed === false
            ? ''
            : accountHolder?.disclosure?.industryEmployed?.firmName,
      }}
      validationSchema={upsertAccountHolderDisclosuresValidation}>
      {form => {
        return (
          <Form autoComplete='off' style={{ width: '100%' }}>
            {isPrimary && (
              <Col span={24}>
                <MFormRadioGroup
                  label='Statement Delivery'
                  align='vertical'
                  value={form.values.statementDelivery}
                  defaultValue={
                    isPrimary
                      ? accountHolder?.disclosure?.statementDelivery?.value ??
                        AccountHolderStatementDeliveryDto.Electronic
                      : AccountHolderStatementDeliveryDto.Electronic
                  }
                  options={STATEMENT_DELIVERY_OPTION_LIST}
                  error={form.errors.statementDelivery}
                  onChange={value => {
                    form.setFieldValue('statementDelivery', value);
                  }}
                  testId={`${testId}-account-statement-delivery`}
                />
              </Col>
            )}

            <Col span={24} className={Styles.titleContainer}>
              <p className={Styles.title} data-testid={`${testId}-account-individual-representation-title`}>
                Individual Representations
              </p>
              <p className={Styles.subtitle} data-testid={`${testId}-account-individual-representation-subtitle`}>
                (Please attest as to whether any of the following apply to you.)
              </p>
            </Col>

            <Col span={24} className={`$${Spacing.mt4} ${form.values.stakeholder ? '' : Spacing.mb16}`}>
              <MFormCheckbox
                label='I am a stakeholder in a publicly traded company'
                checked={form.values.stakeholder}
                defaultChecked={Boolean(accountHolder?.disclosure?.stakeholder)}
                onChange={value => {
                  form.setFieldValue('stakeholder', value.checked);
                }}
                error={form.errors.stakeholder}
                testId={`${testId}-account-stakeholder`}
              />
            </Col>
            {form.values.stakeholder && (
              <Col span={24}>
                <MFormInput
                  label='Company Ticker Symbol(s)'
                  weight='light'
                  placeholder='Enter'
                  defaultValue={
                    accountHolder?.disclosure?.stakeholder === false
                      ? ''
                      : accountHolder?.disclosure?.stakeholder?.tickerSymbol
                  }
                  value={form.values.tickerSymbol}
                  onChange={value => form.setFieldValue('tickerSymbol', value)}
                  error={form.errors.tickerSymbol}
                  name='disclosures-company-ticker-name-attribute'
                  autoComplete='off'
                  testId={`${testId}-account-stakeholder-company-ticker-symbol`}
                />
              </Col>
            )}

            <Col span={24} className={Spacing.mt4}>
              <MFormCheckbox
                label='Are you employed by or a member of a registered broker-dealer, securities or futures exchange, or securities industry regulatory body (e.g., FINRA, NFA)?'
                checked={form.values.industryEmployed}
                defaultChecked={Boolean(accountHolder?.disclosure?.industryEmployed)}
                onChange={value => {
                  form.setFieldValue('industryEmployed', value.checked);
                }}
                testId={`${testId}-account-finra`}></MFormCheckbox>
            </Col>
            {form.values.industryEmployed && (
              <Col span={24}>
                <MFormInput
                  label='Firm Name'
                  weight='light'
                  placeholder='Enter'
                  defaultValue={
                    accountHolder?.disclosure?.industryEmployed === false
                      ? ''
                      : accountHolder?.disclosure?.industryEmployed?.firmName
                  }
                  value={form.values.firmName}
                  onChange={value => form.setFieldValue('firmName', value)}
                  error={form.errors.firmName}
                  name='disclosures-firm-name-attribute'
                  autoComplete='off'
                  testId={`${testId}-account-finra-firm-name`}
                />
              </Col>
            )}

            <Col span={24}>
              <MFormSaveButton<DisclosuresFormValues>
                loading={isUpsertLoading}
                onCancel={_onCancel}
                onSave={_onSave}
                isEditMode={shouldPatch}
                form={form}
                testId={`${testId}-disclosures`}
              />
            </Col>
          </Form>
        );
      }}
    </Formik>
  );
};
